import type { Reference } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import type { NoteTemplate } from '@seen/core/models/noteTemplate'
import { ExtendedObservation } from '@seen/core/models/observation'
import type { Participant } from '@seen/core/models/patients/participant'
import { referenceStringToId } from '@seen/core/references'
import { useQueryClient } from '@tanstack/react-query'
import type { PlaceholderOptions } from '@tiptap/extension-placeholder'
import Placeholder from '@tiptap/extension-placeholder'
import type { Extension, Extensions } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { compact, uniq } from 'lodash-es'
import { useCallback, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { AIProposalsExtension } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { ChoiceNode } from '#src/components/rich-text-editor/choice/ChoiceNode.tsx'
import { EncounterMedicationsBlockNode } from '#src/components/rich-text-editor/EncounterMedicationsBlockNode/EncounterMedicationsBlockNode.tsx'
import {
  getEditorHelpers,
  getRenderExtensions,
  STARTER_KIT_OPTIONS,
} from '#src/components/rich-text-editor/extensions.ts'
import { MentionNode } from '#src/components/rich-text-editor/mention/MentionNode.tsx'
import { ObservationNode } from '#src/components/rich-text-editor/ObservationNode/ObservationNode.tsx'
import { createSuggestionWidgetShells } from '#src/components/rich-text-editor/suggestionWidgetShells.ts'
import { TemplateVariableNode } from '#src/components/rich-text-editor/template-variable/TemplateVariableNode.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useFileHandler } from '#src/hooks/useFileHandler.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

interface ConfigureOpts {
  inlineChoiceSources: boolean
  disableStarterKitHistory?: boolean
  contextParticipant?: Participant
  choiceNodesEditable?: boolean
  enablePractitionerMention?: boolean
  enableOrderTemplateMention?: boolean
  isTemplate?: boolean
  onObservationsChanged: (payload: {
    added: string[]
    removed: string[]
  }) => void
  onObservationReferencePersisted?: () => void
  onMergeTemplateMetadata?: (template: NoteTemplate) => void
}

/* Substitute runtime-configured editor extensions in place of their render-safe
 * defaults from RENDER_EXTENSIONS. Anything not in the switch passes through
 * unchanged (Typography, TaskList, button nodes, etc. — none need
 * editor-only runtime config). */
function configureForEditor(
  ext: Extensions[number],
  opts: ConfigureOpts
): Extensions[number] {
  switch (ext.name) {
    case 'aiProposals':
      /* Inject the host-owned shell manager so pending insert content previews
       * through the real node views (design §1.2). A React host is present here
       * by construction (this runs inside a React hook), so the mounter's
       * ReactRenderer portals resolve against the editor's contentComponent. */
      return AIProposalsExtension.configure({
        createShells: createSuggestionWidgetShells,
        inlineChoiceSources: opts.inlineChoiceSources,
      })
    case 'starterKit':
      return StarterKit.configure({
        ...STARTER_KIT_OPTIONS,
        undoRedo: opts.disableStarterKitHistory ? false : undefined,
      })
    case 'templateVariable':
      return TemplateVariableNode.configure({
        participant: opts.contextParticipant,
      })
    case 'choice':
      return ChoiceNode.configure({
        editable: opts.choiceNodesEditable,
      })
    case 'mention':
      return MentionNode.configure({
        enablePractitionerMention: opts.enablePractitionerMention,
        enableOrderTemplateMention: opts.enableOrderTemplateMention,
        isTemplate: opts.isTemplate,
        onMergeTemplateMetadata: opts.onMergeTemplateMetadata,
      })
    case 'encounter-medications-block':
      return EncounterMedicationsBlockNode.configure({
        patientReferenceString: opts.contextParticipant?.reference.reference,
      })
    case 'observation':
      return ObservationNode.configure({
        participant: opts.contextParticipant?.reference,
        onObservationsChanged: opts.onObservationsChanged,
        onReferencePersisted: opts.onObservationReferencePersisted,
      })
    default:
      return ext
  }
}

interface UseTiptapExtensionsResult {
  extensions: Extensions
  cancelObservationTracking: () => void
}

export function useTiptapExtensions({
  enableFileHandler = true,
  placeholder,
  fileHandlerDocumentReferenceContextRelated,
  contextParticipant,
  choiceNodesEditable,
  enablePractitionerMention,
  enableOrderTemplateMention,
  disableStarterKitHistory,
  disableMention,
  isTemplate,
  enableColumns,
  liveblocksProvidesCommentMark = false,
  onObservationReferencePersisted,
  onMergeTemplateMetadata,
}: {
  enableFileHandler?: boolean
  placeholder?: PlaceholderOptions['placeholder']
  fileHandlerDocumentReferenceContextRelated?: Reference[]
  contextParticipant?: Participant
  choiceNodesEditable?: boolean
  enablePractitionerMention?: boolean
  enableOrderTemplateMention?: boolean
  disableStarterKitHistory?: boolean
  disableMention?: boolean
  isTemplate?: boolean
  enableColumns?: boolean
  /* The host already composes @liveblocks/react-tiptap, whose mark includes
   * thread UI; omit the core render-only copy to avoid a duplicate name. */
  liveblocksProvidesCommentMark?: boolean
  onObservationReferencePersisted?: () => void
  onMergeTemplateMetadata?: (template: NoteTemplate) => void
}): UseTiptapExtensionsResult {
  const fileHandler = useFileHandler(fileHandlerDocumentReferenceContextRelated)
  /* Part of the memo deps, but the plugin reads it once when the editor is
   * built (see the extension's option), so a value that changed after mount
   * would not reach it. The flag snapshot is fixed before any editor mounts —
   * the portal holds its children until the values resolve — which is what
   * keeps that from mattering. */
  const inlineChoiceSources = useFeatureFlag(
    featureFlags.aiChatChoiceInlineSource
  )

  const { onObservationsChanged, cancelObservationTracking } =
    useHandleObservationsChanged()

  /* `contextParticipant` is a model instance re-created on every React Query
   * cache write (see cacheIndividualResources), so its identity churns even when
   * the underlying participant is unchanged. Feeding that identity into the
   * extensions memo rebuilds the array on every cache event, and @tiptap/react
   * then re-runs editor.setOptions() to swap the whole extension set — a steady
   * heap/CPU drain on the visit-note page. Collapse it to a stable reference
   * keyed on the participant's data; the participant-aware nodes
   * (encounter-medications, observation, templateVariable) only read its
   * content, never its identity. */
  const stableContextParticipant = useMemoValue(contextParticipant)

  const extensions = useMemo(() => {
    const configured = getRenderExtensions()
      .filter((ext) => {
        if (disableMention && ext.name === 'mention') {
          return false
        }
        if (
          !enableColumns &&
          (ext.name === 'columnBlock' || ext.name === 'column')
        ) {
          return false
        }
        if (
          liveblocksProvidesCommentMark &&
          ext.name === 'liveblocksCommentMark'
        ) {
          return false
        }
        return true
      })
      .map((ext) =>
        configureForEditor(ext, {
          inlineChoiceSources,
          disableStarterKitHistory,
          contextParticipant: stableContextParticipant,
          choiceNodesEditable,
          enablePractitionerMention,
          enableOrderTemplateMention,
          isTemplate,
          onObservationsChanged,
          onObservationReferencePersisted,
          onMergeTemplateMetadata,
        })
      )

    const editorHelpers = getEditorHelpers().map((ext) => {
      if (ext.name === 'placeholder') {
        return Placeholder.configure({
          placeholder,
        }) as Extension<PlaceholderOptions>
      }
      return ext
    })

    return compact([
      ...configured,
      enableFileHandler ? fileHandler : undefined,
      ...editorHelpers,
    ])
  }, [
    placeholder,
    enableFileHandler,
    fileHandler,
    disableStarterKitHistory,
    stableContextParticipant,
    choiceNodesEditable,
    enablePractitionerMention,
    enableOrderTemplateMention,
    disableMention,
    isTemplate,
    enableColumns,
    onObservationsChanged,
    onObservationReferencePersisted,
    onMergeTemplateMetadata,
    liveblocksProvidesCommentMark,
    inlineChoiceSources,
  ])
  return { extensions, cancelObservationTracking }
}

const OBSERVATIONS_CHANGED_DEBOUNCE_MS = 300

interface UseHandleObservationsChangedResult {
  onObservationsChanged: (payload: {
    added: string[]
    removed: string[]
  }) => void
  cancelObservationTracking: () => void
}

function useHandleObservationsChanged(): UseHandleObservationsChangedResult {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const transactionMutateAsync = useTransactionMutation().mutateAsync

  const pendingAdded = useRef<Set<string>>(new Set())
  const pendingRemoved = useRef<Set<string>>(new Set())
  const ignoreChanges = useRef(false)

  const syncObservations = useDebouncedCallback(async () => {
    const added = [...pendingAdded.current]
    const removed = [...pendingRemoved.current]
    pendingAdded.current = new Set()
    pendingRemoved.current = new Set()

    if (!added.length && !removed.length) {
      return
    }

    const referenceStrings = uniq([...added, ...removed])
    if (!referenceStrings.length) {
      return
    }

    let observations: ExtendedObservation[] = []

    try {
      observations = await queryClient.fetchQuery(
        searchOptions(medplum, ExtendedObservation, {
          _id: referenceStrings.map(referenceStringToId),
        })
      )
    } catch (error) {
      toast.error(
        `Failed to fetch observations for status update${error instanceof Error ? `: ${error.message}` : ''}`
      )
      throw error
    }

    const removedSet = new Set(removed)
    const patches = observations
      .map((observation) => {
        const desiredStatus = removedSet.has(observation.reference.reference)
          ? ('entered-in-error' as const)
          : ('final' as const)

        if (observation.status === desiredStatus) {
          // Already in the correct state → no-op
          return null
        }

        return { observation, desiredStatus }
      })
      .filter((x) => x !== null)

    if (!patches.length) {
      return
    }

    try {
      await transactionMutateAsync(async (txn) => {
        patches.forEach(({ observation, desiredStatus }) => {
          txn.patch(observation, (o) => {
            o.status = desiredStatus
          })
        })
      })
    } catch (error) {
      toast.error(
        `Failed to update observation statuses${error instanceof Error ? `: ${error.message}` : ''}`
      )
      throw error
    }
  }, OBSERVATIONS_CHANGED_DEBOUNCE_MS)

  const cancelObservationTracking = useCallback(() => {
    // Set flag to ignore all future observation changes
    ignoreChanges.current = true
    // Cancel the debounced sync
    syncObservations.cancel()
    // Clear pending changes
    pendingAdded.current = new Set()
    pendingRemoved.current = new Set()
  }, [syncObservations])

  const onObservationsChanged = useCallback(
    ({ added, removed }: { added: string[]; removed: string[] }) => {
      // If ignoreChanges flag is set, only ignore removals (not additions)
      // When user starts composing a new note and adds observations, we want to track them
      if (ignoreChanges.current) {
        if (added.length > 0) {
          // User is adding new observations, re-enable tracking
          ignoreChanges.current = false
        } else if (removed.length > 0) {
          // Still in ignore mode, skip removals
          return
        }
      }

      for (const ref of added) {
        pendingRemoved.current.delete(ref)
        pendingAdded.current.add(ref)
      }
      for (const ref of removed) {
        pendingAdded.current.delete(ref)
        pendingRemoved.current.add(ref)
      }

      void syncObservations()
    },
    [syncObservations]
  )

  return { onObservationsChanged, cancelObservationTracking }
}
